import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon } from '@atgfr8/icons';
import { Button } from '@components';
import { CarrierReactivationRequestCacheItemT } from '@store/carriers';
import { openModal } from '@store/modals';
import { Carrier, carrierStore, setCarrierStore } from '@store/orders';
import { load } from '@store/orders/selectors';
import { formatDateString } from '@utils/dateFormat';
import { ReactivationRequestModal } from '@views/carriers/carrierDetails/carrierDetailsTab/components/ReactivationRequestModal';
import { createEffect, createSignal, Show } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import classes from './classes';

export const ReactivationRow = () => {
  const [showComment, setShowComment] = createSignal(false);

  const carrierId = () => load()?.carrierId;
  const loadId = () => load()?.id;
  const modalId = () => `reactivation-request-load-${loadId() ?? carrierId()}`;

  const storeCacheItem = () => {
    const id = carrierId();
    if (id == null) return null;
    return (
      (carrierStore.carrier as Array<Carrier | undefined>).find(
        (c) => c?.id === id,
      )?.carrierReactivationRequestCacheItemViewModel ?? null
    );
  };

  const [cacheItem, setCacheItem] =
    createSignal<CarrierReactivationRequestCacheItemT | null>(storeCacheItem());
  createEffect(() => setCacheItem(storeCacheItem()));

  const isPending = () => Boolean(cacheItem());
  const hasComment = () => Boolean(cacheItem()?.comment);

  const onRequest = (item: CarrierReactivationRequestCacheItemT) => {
    const index = (
      carrierStore.carrier as Array<Carrier | undefined>
    ).findIndex((c) => c?.id === carrierId());
    if (index > -1) {
      setCarrierStore(
        'carrier',
        index,
        'carrierReactivationRequestCacheItemViewModel',
        item,
      );
    }
    setCacheItem(item);
  };

  return (
    <>
      <Stack direction="column" class="w-full gap-y-2">
        <Stack
          direction="row"
          class="items-start justify-between gap-x-3 w-full"
        >
          <Stack direction="column">
            <Show
              when={isPending()}
              fallback={
                <>
                  <div class={classes.rowTitle}>Reactivation Required</div>
                  <div class={classes.rowText}>
                    Request compliance review to reactivate this carrier.
                  </div>
                </>
              }
            >
              <div class="text-[14px] font-medium leading-[1.5] tracking-[0.15px] text-[#026ea1]">
                Reactivation Requested
              </div>
              <div class={classes.rowText}>
                Reactivation request has been submitted for review. Updates will
                be sent to the user who requested this reactivation.
              </div>
              <Stack
                direction="row"
                class="mt-1 flex-wrap items-center gap-x-2 text-[12px] text-[rgba(0,0,0,0.6)]"
              >
                <span class="font-medium tracking-[0.14px]">Requested By</span>
                <span class="tracking-[0.4px]">
                  {cacheItem()?.requestedByUserName}{' '}
                  {formatDateString(
                    cacheItem()?.requestedAtUtc ?? '',
                    'MM/dd/yyyy t',
                  )}
                </span>
              </Stack>
            </Show>
          </Stack>
          <Stack direction="row" class="shrink-0 items-center gap-x-1">
            <Button
              label="Request Reactivation"
              variant="outlined"
              size="sm"
              class="shrink-0 bg-white! border! border-primary! text-primary! rounded! hover:bg-primary/[0.06]! disabled:bg-white! disabled:border-[rgba(0,0,0,0.12)]! disabled:text-[rgba(0,0,0,0.38)]! disabled:hover:bg-white!"
              dataTestId="request-reactivation-button"
              disabled={isPending()}
              onClick={() => openModal(modalId())}
            />
            <Show when={isPending() && hasComment()}>
              <button
                type="button"
                class="flex items-center justify-center rounded-full p-1.25 text-[#026ea1] hover:bg-black/4"
                aria-label={showComment() ? 'Hide comment' : 'Show comment'}
                aria-expanded={showComment()}
                onClick={() => setShowComment((prev) => !prev)}
                data-testid="reactivation-comment-toggle"
              >
                <span
                  class="inline-flex transition-transform duration-200"
                  style={{
                    transform: `rotate(${showComment() ? 180 : 0}deg)`,
                  }}
                >
                  <MenuDownIcon />
                </span>
              </button>
            </Show>
          </Stack>
        </Stack>
        <Show when={isPending() && hasComment() && showComment()}>
          <Box class="border-t border-[rgba(0,0,0,0.12)] pt-2">
            <div class="text-[12px] font-medium text-[rgba(0,0,0,0.87)] tracking-[0.14px]">
              Comment
            </div>
            <div class="mt-1 whitespace-pre-wrap wrap-break-word text-[12px] text-[rgba(0,0,0,0.87)] tracking-[0.4px]">
              {cacheItem()?.comment}
            </div>
          </Box>
        </Show>
      </Stack>
      <ReactivationRequestModal
        modalId={modalId()}
        carrierId={carrierId()}
        loadId={loadId()}
        onRequested={onRequest}
        changeTrackingSource={
          CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsRequestReactivation
        }
      />
    </>
  );
};
